import { useCallback, useEffect, useRef, useState, type ChangeEvent, type CompositionEvent, type KeyboardEvent } from 'react';
import { normalizeProductSearchQuery, PRODUCT_SEARCH } from '@longjie/shared';

/** Shared keyword controls for the warehouse-based product-search hierarchy. */
export function useProductSearch({ onCommit, initialValue = '' }: {
  onCommit?: () => void;
  initialValue?: string;
} = {}) {
  const [input, setInput] = useState(initialValue);
  const [query, setQuery] = useState(normalizeProductSearchQuery(initialValue));
  const [composing, setComposing] = useState(false);
  const [revision, setRevision] = useState(0);
  const composingRef = useRef(false);
  const onCommitRef = useRef(onCommit);
  useEffect(() => { onCommitRef.current = onCommit; }, [onCommit]);

  useEffect(() => {
    if (composing || normalizeProductSearchQuery(input) === query) return;
    const timer = window.setTimeout(() => {
      setQuery(normalizeProductSearchQuery(input));
      onCommitRef.current?.();
    }, PRODUCT_SEARCH.delayMs);
    return () => window.clearTimeout(timer);
  }, [input, query, composing]);

  const submit = useCallback(() => {
    if (composingRef.current) return;
    setQuery(normalizeProductSearchQuery(input));
    setRevision((value) => value + 1);
    onCommitRef.current?.();
  }, [input]);

  const reset = useCallback((value: string) => {
    composingRef.current = false;
    setComposing(false);
    setInput(value);
    setQuery(normalizeProductSearchQuery(value));
  }, []);

  const clear = useCallback(() => {
    reset('');
    setRevision((value) => value + 1);
    onCommitRef.current?.();
  }, [reset]);

  return {
    input, query, composing, revision, submit, reset, clear,
    pending: composing || normalizeProductSearchQuery(input) !== query,
    inputProps: {
      value: input,
      maxLength: PRODUCT_SEARCH.maxLength,
      onChange: (event: ChangeEvent<HTMLInputElement>) => setInput(event.target.value),
      onCompositionStart: () => { composingRef.current = true; setComposing(true); },
      onCompositionEnd: (event: CompositionEvent<HTMLInputElement>) => {
        setInput(event.currentTarget.value);
        composingRef.current = false;
        setComposing(false);
      },
      onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        if (!event.nativeEvent.isComposing && event.nativeEvent.keyCode !== 229) submit();
      },
    },
  };
}

export type ProductSearchController = ReturnType<typeof useProductSearch>;
